/**
 * <Byline /> — surface the editorial byline directly under the page H1.
 *
 * 2026-02-10 pass 71 — Now defaults to an institutional editorial byline
 * (HavenCostGuide Editorial Team) rather than named personas. When the
 * author and reviewer resolve to the same entity (the typical institutional
 * case), we render a single combined line rather than the duplicated
 * "Written by X / Reviewed by Y" pattern that would look redundant.
 *
 * For E-E-A-T: the byline links to /editorial-standards, which documents
 * the team's sourcing, fact-checking, corrections policy, and conflict-of-
 * interest disclosure. That page is the institutional equivalent of a
 * named-expert author profile.
 */
import { Link } from "react-router-dom";
import { ShieldCheck, PenLine } from "lucide-react";
import { DEFAULT_BYLINE } from "@/lib/editorial";

export default function Byline({
  author,
  reviewer,
  dateLabel,
  dateISO,
  compact = false,
  testid = "byline",
}) {
  const a = author || DEFAULT_BYLINE.author;
  const r = reviewer || DEFAULT_BYLINE.reviewer;
  const sameEntity = a.id === r.id;

  if (compact) {
    return (
      <p className="mt-2 text-xs text-slate-500" data-testid={testid}>
        <span className="inline-flex items-center gap-1">
          <PenLine className="h-3 w-3" aria-hidden="true" />
          By{" "}
          <Link to={a.profile} className="font-semibold text-slate-700 hover:text-emerald-700">
            {a.name}
          </Link>
        </span>
        {!sameEntity && (
          <>
            <span className="mx-2 text-slate-300">·</span>
            <span className="inline-flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" aria-hidden="true" />
              Reviewed by{" "}
              <Link to={r.profile} className="font-semibold text-slate-700 hover:text-emerald-700">
                {r.name}
              </Link>
            </span>
          </>
        )}
        {dateLabel && (
          <>
            <span className="mx-2 text-slate-300">·</span>
            <time dateTime={dateISO}>{dateLabel}</time>
          </>
        )}
      </p>
    );
  }

  // Institutional (single-entity) byline — renders one combined line.
  if (sameEntity) {
    return (
      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm" data-testid={testid}>
        <div className="inline-flex items-center gap-2">
          <PenLine className="h-4 w-4 text-emerald-700" aria-hidden="true" />
          <span className="text-slate-500">By</span>
          <Link to={a.profile} className="font-semibold text-slate-900 hover:text-emerald-700" data-testid={`${testid}-author`}>
            {a.name}
          </Link>
          <span className="text-xs text-slate-500">· {a.role}</span>
        </div>
        {dateLabel && (
          <div className="text-xs text-slate-500">
            Last reviewed <time dateTime={dateISO}>{dateLabel}</time>
          </div>
        )}
      </div>
    );
  }

  // Future-proof — if topic-overrides ever resume naming distinct human
  // author + reviewer, render the original two-line format.
  return (
    <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm" data-testid={testid}>
      <div className="inline-flex items-center gap-2">
        <PenLine className="h-4 w-4 text-emerald-700" aria-hidden="true" />
        <span className="text-slate-500">Written by</span>
        <Link to={a.profile} className="font-semibold text-slate-900 hover:text-emerald-700" data-testid={`${testid}-author`}>
          {a.name}
        </Link>
        <span className="text-xs text-slate-500">· {a.role}</span>
      </div>
      <div className="inline-flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-emerald-700" aria-hidden="true" />
        <span className="text-slate-500">Reviewed by</span>
        <Link to={r.profile} className="font-semibold text-slate-900 hover:text-emerald-700" data-testid={`${testid}-reviewer`}>
          {r.name}
        </Link>
        <span className="text-xs text-slate-500">· {r.role}</span>
      </div>
      {dateLabel && (
        <div className="text-xs text-slate-500">
          Last reviewed <time dateTime={dateISO}>{dateLabel}</time>
        </div>
      )}
    </div>
  );
}
